import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { EyeOff, Loader2 } from "lucide-react";

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { formatDateBR } from "@/shared/utils/datetime";
import { LearningsService } from "../services/learningsService";
import type { Learning } from "../types/LearningTypes";

interface LearningReferencesSheetProps {
  learning: Learning | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * As mensagens de onde a proposta nasceu (AB#87286). Só o texto das conversas de quem
 * abre: as de outra pessoa aparecem como "existe", sem conteúdo.
 */
export const LearningReferencesSheet = ({ learning, onOpenChange }: LearningReferencesSheetProps) => {
  const { t } = useTranslation();
  const { data: references = [], isLoading, isError } = useQuery({
    queryKey: ["learning-references", learning?.id],
    queryFn: () => LearningsService.references(learning!.id),
    enabled: !!learning,
  });

  return (
    <Sheet open={!!learning} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t("learnings.references.title")}</SheetTitle>
          <SheetDescription>{t("learnings.references.description")}</SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">{t("learnings.references.loadError")}</p>
        ) : references.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("learnings.references.empty")}</p>
        ) : (
          <ul className="space-y-3 overflow-y-auto">
            {references.map((reference) => (
              <li key={reference.message_id} className="rounded-md border p-3">
                <p className="mb-1 text-xs text-muted-foreground">{formatDateBR(reference.at)}</p>
                {reference.visible ? (
                  <p className="whitespace-pre-wrap text-sm">{reference.text}</p>
                ) : (
                  <p className="flex items-center gap-2 text-sm italic text-muted-foreground">
                    <EyeOff className="h-4 w-4" />
                    {t("learnings.references.hidden")}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
};
